
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import AbstractField from '@shared/components/forms/inputs/abstract-field/abstract-field';
import FieldWrapper from '@shared/components/forms/inputs/field-wrapper/field-wrapper.vue';
import TableFooter from '@shared/components/tables/table-footer/table-footer.vue';
import TableMock from '@shared/components/mocks/table-mock/table-mock.vue';
import PaginationInterface from '@core/interfaces/pagination-interface';
import { FilterItem } from '@core/interfaces/filter-item-interface';
import { Invoice } from '@accounting/interfaces/invoice';
import { Observable } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import DisplayFieldCode from '@shared/components/displays/display-field-code/display-field-code.vue';
import moment from 'moment';

@Component({
  name: 'bpc-credit-note-clearance-table',
  components: {
    'bpc-display-field-code': DisplayFieldCode,
    'bpc-field-wrapper': FieldWrapper,
    'bpc-table-footer': TableFooter,
    'bpc-table-mock': TableMock,
  },
})
export default class CreditNoteClearanceTable extends mixins(AbstractField) {
  @Prop({
    type: [Object, Array],
    default() {
      return [];
    },
  })
  readonly filters: FilterItem[];

  // Data
  public observableSubscription = null;
  public fields = [
    {
      key: 'documentNumber',
      label: this.$t('global.document_number.label'),
      sortable: false,
      thClass: 'w-30',
    },
    {
      key: 'invoiceDate',
      label: this.$t('global.date.label'),
      sortable: false,
      thClass: 'w-30',
      formatter: value => {
        return this.formatDate(value);
      },
    },
    {
      key: 'balance',
      label: this.$t('global.balance.label'),
      sortable: false,
      thClass: 'w-30',
      formatter: value => {
        return this.formatAmount(value);
      },
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      thClass: 'w-10',
    },
  ];
  public loading: boolean = false;
  public confirming: boolean = false;
  public pagination: PaginationInterface = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    from: 0,
    to: 0,
    total: 0,
  };
  public creditNotes = [];
  public selectedCreditNote: Invoice = null;
  //FIXME this makes this component tied to the invoice form only. If we want to use it somewhere else, we will need to find a way to take the invoice as input.
  private invoice: Invoice = this.formModel as Invoice;

  mounted() {
    this.fetchCreditNotes();
  }

  get confirmModalTitle() {
    return this.$t('global.credit_note_clearance.confirm_modal.title');
  }

  get confirmModalBody() {
    const amount = Math.min(this.invoice.balance, Math.abs(this.selectedCreditNote?.balance));
    return this.$t('global.credit_note_clearance.confirm_modal.body', {
      invNum: this.invoice.document_number,
      cnNum: this.selectedCreditNote?.code,
      amount: amount,
    });
  }

  clickOnPay(creditNote) {
    this.selectedCreditNote = creditNote;
    this.confirming = false;
    this.$bvModal.show('modal-confirm-credit-note-clearance');
  }

  onCloseConfirmModal() {
    this.selectedCreditNote = null;
    this.$bvModal.hide('modal-confirm-credit-note-clearance');
  }

  onConfirmClearance() {
    this.confirming = true;
    const path = `${this.$bpcApiRouteHelper.getApiPath(
      `invoices/${this.invoice.id}/credit-note-clearance`,
    )}`;
    this.$bpcApi.post(path, { creditNoteId: this.selectedCreditNote.id }).subscribe(
      res => {
        this.confirming = false;
        this.$bpcMessage.showSuccessMessage({ text: 'global.generic.successful_action' });
        this.$bvModal.hide('modal-confirm-credit-note-clearance');
        this.$root.$emit('updateClearanceFromInvoiceForm');
      },
      error => {
        this.confirming = false;
        this.$bpcMessage.showErrorMessage({ text: 'global.generic.error_form_action' });
      },
    );
  }

  fetchCreditNotes() {
    this.loading = true;
    const path = `${this.$bpcApiRouteHelper.getApiPath('invoices/credit-notes-to-clear')}`;
    const params = this.buildQueryParams();
    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(path, {
      params: params,
    });
    if (this.observableSubscription) {
      this.observableSubscription.unsubscribe();
    }

    this.observableSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.creditNotes = res.data;

        if (res.meta) {
          this.pagination = res.meta;
        }

        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  buildQueryParams(): object {
    return {
      page: this.pagination.current_page,
      per_page: this.pagination.per_page,
      orderBy: 'id',
      orderDirection: 'desc',
      contactId: this.invoice.contact_id,
    };
  }

  formatDate(date: string): string {
    if (date) {
      return moment(date).format('DD/MM/YYYY');
    }
  }

  formatAmount(value: number) {
    return value.toFixed(2).replace('.', ',') + ' €';
  }
}
